import type { ResponseTaskData, Contact } from '../models';
import { Task } from '../models';
import { TaskQueryParams } from '@stuut/api/query';
import { ResourceWithAttachments } from './attachments';
import type { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import pluralize from 'pluralize';

interface EmailFormData {
  body?: string;
  from_?: string;
  to?: string[];
  subject?: string;
  cc?: string[];
}

interface AddNewContactData {
  email: string;
  given_name: string | undefined;
  family_name: string | undefined;
}

const capitalize = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

export class Tasks extends ResourceWithAttachments<Task> {
  model = Task;

  resourcePath = 'v1/tasks';

  async status() {
    try {
      return this.api.get(this.path('status'));
    } catch (error) {
      console.error(error);
    }
  }

  async close(
    task: Task,
    closeReason?: string,
    closeReasonNote?: string,
    data?: Record<string, any>,
  ): Promise<Task | undefined> {
    return this.handleTaskAction(task, task?.isDisputeActivate ? 'close_with_action' : 'close', {
      request_data: data,
      close_reason: this.getCloseReason(task, 'dismissed', closeReason, closeReasonNote),
    });
  }

  async handleCompleteAction(
    task: Task,
    request_data?: Record<string, any>,
  ): Promise<Task | undefined> {
    if (task.isExpectsAcknowledge) {
      return this.acknowledge(task, request_data);
    }

    if (task.isExpectsComplete) {
      return this.complete(task, request_data);
    }

    if (task.isExpectsCompleteWithAction) {
      return this.completeWithAction(task, request_data);
    }
  }

  async complete(task: Task, request_data?: Record<string, any>): Promise<Task | undefined> {
    return this.handleTaskAction(task, 'complete', {
      request_data,
    });
  }

  async acknowledge(task: Task, request_data?: Record<string, any>): Promise<Task | undefined> {
    return this.handleTaskAction(task, 'acknowledge', {
      request_data,
    });
  }

  async completeWithAction(
    task: Task,
    request_data?: Record<string, any>,
  ): Promise<Task | undefined> {
    return this.handleTaskAction(task, 'complete_with_action', {
      request_data,
    });
  }

  async updateAssignee(task: Task, assignee: string | null): Promise<Task | undefined> {
    const response = await this.api.post(this.path(task.id, 'set-assignee'), {
      data: { assignee },
    });

    const partner = task.partner;
    task.updateFromJson(response.data);
    task.partner = partner;

    return task;
  }

  async removeAssignee(task: Task): Promise<Task | undefined> {
    return this.updateAssignee(task, null);
  }

  async bulkAction(
    taskIds: string[],
    actionType,
    closeReason?: string,
    closeReasonNote?: string,
    tasks?: Task[],
    data?: Record<string, any>,
  ): Promise<Task[]> {
    const closeType = actionType === 'close' ? 'dismissed' : 'marked inaccurate';

    const response = await this.api.post(this.path('bulk-action'), {
      data: {
        request_data: data,
        action_type: 'close',
        tasks: taskIds,
        close_reason: this.getBulkCloseReason(taskIds, closeType, closeReason, closeReasonNote),
      },
    });

    if (tasks) {
      tasks.forEach((task) => {
        const taskResponse = response?.data?.tasks?.find((res) => res.id === task.id);
        if (taskResponse) task.updateFromJson(taskResponse);
      });

      return tasks;
    }

    return response.data?.tasks?.map((task) => Task.fromJson(task));
  }

  async bulkCompleteAction(
    taskIds: string[],
    action_type: 'acknowledge' | 'complete' | 'complete_with_action',
    task_type?: string,
    tasks?: Task[],
  ): Promise<Task[]> {
    const pastTense = {
      complete: 'completed',
      acknowledge: 'acknowledged',
      complete_with_action: 'completed with action',
    }[action_type];
    const response = await this.api.post(this.path('bulk-action'), {
      data: {
        action_type,
        tasks: taskIds,
        close_reason: `Bulk ${pastTense} (${pluralize('task', taskIds.length, true)})`,
        task_type,
      },
    });

    if (tasks) {
      tasks.forEach((task) => {
        const taskResponse = response?.data?.tasks?.find((res) => res.id === task.id);
        if (taskResponse) task.updateFromJson(taskResponse);
      });

      return tasks;
    }

    return response.data?.tasks?.map((task) => Task.fromJson(task));
  }

  async sendEmailResponse(
    task: Task,
    emailData: EmailFormData,
    responseTaskData: ResponseTaskData,
  ): Promise<Task | undefined> {
    const requestData = {
      cc: emailData.cc || responseTaskData.data.cc,
      from_: emailData.from_ || responseTaskData.data.from_,
      html_body: emailData.body || responseTaskData.data.html_body,
      subject: emailData.subject || responseTaskData.data.subject,
      to: emailData.to || responseTaskData.data.to,
    };

    return this.completeWithAction(task, requestData);
  }

  async sendNewResponse(
    task: Task,
    emailData: EmailFormData,
    responseTaskData: ResponseTaskData,
  ): Promise<Task | undefined> {
    const requestData = {
      cc: emailData.cc,
      from_: emailData.from_,
      html_body: emailData.body || responseTaskData.data.html_body,
      subject: emailData.subject || responseTaskData.data.subject,
      to: emailData.to,
    };

    return this.completeWithAction(task, requestData);
  }

  async attachInvoice(task: Task, invoice_id: string): Promise<Task> {
    const response = await this.api.post(this.path(task?.id, 'attach-invoice'), {
      data: { invoice_id },
    });

    task.updateFromJson(response.data);

    return task;
  }

  async attachCreditMemo(task: Task, credit_memo_id: string): Promise<Task> {
    const response = await this.api.post(this.path(task?.id, 'attach-credit-memo'), {
      data: { credit_memo_id },
    });

    task.updateFromJson(response.data);

    return task;
  }

  async addNewContact(task: Task, contact?: Contact): Promise<Task | undefined> {
    const requestData: AddNewContactData = {
      email: contact?.email ?? '',
      given_name: contact?.given_name ?? undefined,
      family_name: contact?.family_name ?? undefined,
    };

    return this.completeWithAction(task, requestData);
  }

  async markInaccurate(
    task: Task,
    closeReason?: string,
    closeReasonNote?: string,
    data?: Record<string, any>,
  ): Promise<Task | undefined> {
    return this.handleTaskAction(task, 'close', {
      request_data: data,
      close_reason: this.getCloseReason(task, 'marked inaccurate', closeReason, closeReasonNote),
    });
  }

  async saveResponse(task: Task, data: Record<string, any>) {
    const response = await this.api.post(this.path(task.id, 'update-response'), {
      data,
    });

    task.updateFromJson(response.data);

    return task;
  }

  async handleTaskAction(
    task: Task,
    action_type: string,
    data?: Record<string, any>,
  ): Promise<Task | undefined> {
    const response = await this.api.post(this.path(task.id), {
      data: {
        ...data,
        action_type,
      },
    });

    task.updateFromJson(response.data);

    return task;
  }

  getCloseReason(_task: Task, closeType: string, closeReason?: string, closeReasonNote?: string) {
    let reasonText = capitalize(closeType);
    if (closeReason) reasonText += ` · ${closeReason}`;
    if (closeReasonNote) reasonText += ` · ${closeReasonNote}`;
    return reasonText;
  }

  getBulkCloseReason(
    taskIds: string[],
    closeType: string,
    closeReason?: string,
    closeReasonNote?: string,
  ) {
    let reasonText = `Bulk ${closeType} (${pluralize('task', taskIds.length, true)})`;
    if (closeReason) reasonText += ` · ${closeReason}`;
    if (closeReasonNote) reasonText += ` · ${closeReasonNote}`;
    return reasonText;
  }

  getFormattedNow() {
    return dayjs().utc().format('YYYY-MM-DD HH:mm');
  }

  getDaysOpen(task: Task): number {
    if (!task.created_at) return 0;

    const today = dayjs().utc();
    const createdDate = dayjs(task.created_at).utc();
    return today.diff(createdDate, 'days');
  }

  async generateEmail(task: Task, emailTemplateId: string) {
    const response = await this.api.post(this.path(task.id, 'email-generate'), {
      data: { email_template_id: emailTemplateId },
    });

    task.updateFromJson(response.data);

    return response.data;
  }

  async regenerate(taskId: string, task: Task) {
    const response = await this.api.post(this.path(taskId, 'regenerate'));

    if (task?.id) task.updateFromJson(response.data);

    return response.data;
  }
}
